import { FC, useMemo, useState } from 'react';
import type { VendasPayload } from '@/types/dashboards';
import {
  divergenciasDeCodigo,
  kpisDoMes,
  mesDoPayload,
  rankingUf,
  serieMensal,
  tabelaSku,
  type Medida,
} from '@/utils/equiplexVendas';
import { mesVigente } from '@/utils/filtroPaineis';
import { fmtData, periodoCurto } from '@/utils/painelFormat';
import type { CoresPainel } from '../chartTheme';
import RotuloMes from '../RotuloMes';
import AvisosVendas from './AvisosVendas';
import ForecastFaturadoChart from './ForecastFaturadoChart';
import KpisVendas from './KpisVendas';
import SkuVendasTable from './SkuVendasTable';
import UfChart from './UfChart';

interface Props {
  payload: VendasPayload;
  cores: CoresPainel;
}

const MEDIDAS: { id: Medida; rotulo: string }[] = [
  { id: 'qtd', rotulo: 'Unidades' },
  { id: 'valor', rotulo: 'R$' },
];

function sufixo(m: VendasPayload['meses'][number]): string | null {
  if (m.em_andamento) return 'parcial';
  if (m.futuro) return 'futuro';
  if (!m.tem_forecast) return 'sem forecast';
  return null;
}

const SeletorMes: FC<{ payload: VendasPayload; mes: string; onMes: (m: string) => void }> = ({ payload, mes, onMes }) => (
  <div role="group" aria-label="Mês de produção" className="flex flex-wrap items-center gap-1.5">
    <span className="mr-1 text-xs uppercase tracking-wide text-content-muted">Mês</span>
    {payload.meses.map((m) => {
      const ativo = m.key === mes;
      const extra = sufixo(m);
      return (
        <button
          key={m.key}
          type="button"
          aria-pressed={ativo}
          title={`${m.label}: ${m.range}`}
          onClick={() => onMes(m.key)}
          className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors ${
            ativo
              ? 'border-accent bg-accent font-semibold text-accent-contrast'
              : 'border-theme-border text-content-secondary hover:text-content-primary'
          }`}
        >
          <RotuloMes label={m.label} periodo={periodoCurto(m)} />
          {extra && <span className="font-normal">· {extra}</span>}
        </button>
      );
    })}
  </div>
);

const SeletorMedida: FC<{ medida: Medida; onMedida: (m: Medida) => void }> = ({ medida, onMedida }) => (
  <div role="group" aria-label="Medida" className="inline-flex rounded-lg border border-theme-border p-0.5 text-xs">
    {MEDIDAS.map((m) => (
      <button
        key={m.id}
        type="button"
        aria-pressed={medida === m.id}
        onClick={() => onMedida(m.id)}
        className={`rounded-md px-3 py-1.5 ${
          medida === m.id ? 'bg-surface-hover font-medium text-content-primary' : 'text-content-muted hover:text-content-primary'
        }`}
      >
        {m.rotulo}
      </button>
    ))}
  </div>
);

/**
 * Forecast e vendas: o "Forecast por SKU/UF" do Manus com dado real. Em cima, o mês contra
 * o forecast; no meio, forecast × faturado mês a mês e as UFs (12 meses e o mês, uma embaixo da outra,
 * abertas de cara — o que precisa de clique, ninguém vê); embaixo, o SKU a SKU. Período e
 * produto vêm do filtro dos painéis (servidor); o mês dentro do período é estado local.
 */
const PainelVendas: FC<Props> = ({ payload, cores }) => {
  const [mesEscolhido, setMes] = useState<string | null>(null);
  const mes = mesVigente(mesEscolhido, payload.meses, payload.mes_padrao);
  const [medida, setMedida] = useState<Medida>('qtd');

  const mesDesc = mesDoPayload(payload, mes) ?? payload.meses[payload.meses.length - 1];
  const serie = useMemo(() => serieMensal(payload, medida), [payload, medida]);
  const kpis = useMemo(() => kpisDoMes(payload, mes, medida), [payload, mes, medida]);
  const tabela = useMemo(() => tabelaSku(payload, mes, medida), [payload, mes, medida]);
  const divergencias = useMemo(() => divergenciasDeCodigo(tabela.linhas), [tabela]);
  const ufsJanela = useMemo(() => rankingUf(payload, 'janela', medida), [payload, medida]);
  const ufsMes = useMemo(() => rankingUf(payload, mes, medida), [payload, mes, medida]);
  const janela = payload.ufs.janela;
  // Sem período escolhido, os últimos 12 meses; com período, o dele (snapshot antigo não traz o rótulo).
  const rotuloJanela = janela.rotulo ?? 'últimos 12 meses';

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {payload.meses.length > 1 ? <SeletorMes payload={payload} mes={mes} onMes={setMes} /> : <span />}
        <SeletorMedida medida={medida} onMedida={setMedida} />
      </div>
      <AvisosVendas
        frescor={payload.frescor}
        forecast={payload.forecast}
        mes={mesDesc}
        corte={payload.corte}
        divergencias={divergencias}
      />
      {kpis && <KpisVendas kpis={kpis} medida={medida} corte={payload.corte} />}

      <ForecastFaturadoChart
        serie={serie}
        medida={medida}
        mesSelecionado={mes}
        forecast={payload.forecast}
        corte={payload.corte}
        cores={cores}
      />

      {/* Um embaixo do outro (26/10/2026): o ranking da janela longa e o do mês, cada um na largura toda. */}
      <UfChart
        testId="vendas-uf-janela"
        titulo={`Faturado por UF — ${rotuloJanela}`}
        subtitulo={`${fmtData(janela.inicio)} a ${fmtData(janela.fim)}, por estado de destino da nota.`}
        linhas={ufsJanela}
        medida={medida}
        vazio={`Nenhuma nota ${rotuloJanela === 'no período' ? 'no período' : `nos ${rotuloJanela}`}.`}
        cores={cores}
      />
      <UfChart
        testId="vendas-uf-mes"
        titulo={`Faturado por UF — ${mesDesc.label}`}
        subtitulo={mesDesc.em_andamento ? `${mesDesc.range}, notas até ${fmtData(payload.corte)}.` : mesDesc.range}
        linhas={ufsMes}
        medida={medida}
        vazio={mesDesc.futuro ? 'O mês ainda não começou.' : 'Nenhuma nota neste mês.'}
        cores={cores}
      />

      <SkuVendasTable tabela={tabela} medida={medida} mesLabel={mesDesc.label} janelaUf={rotuloJanela} />

      <details className="rounded-xl border border-theme-border bg-surface-card p-5 text-sm" data-testid="vendas-notas">
        <summary className="cursor-pointer font-semibold text-content-primary">Como os números são calculados</summary>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-content-secondary">
          {payload.notas.map((n) => (
            <li key={n}>{n}</li>
          ))}
          <li>
            Fonte: réplica MySQL do Datasul (notas carregadas todo dia às 22:00) e Balanceamento de Linha; painel gerado às
            06:00.
          </li>
        </ul>
      </details>
    </>
  );
};

export default PainelVendas;
